/**
 * Transient notices — the ones that belong beside what you were doing, not inside it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT THE ALARM TOAST STORE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `alarm-feed.ts` already has toasts, and they are a different thing wearing the same
 * clothes. An alarm toast reports the PLANT — it arrives unasked, it is one of a stream, it
 * has a severity, and it is kept afterwards in a bell so nothing is missed. These report
 * the APP's response to something the reader just did: a sign-in refused, a link sent. They
 * arrive one at a time, they are caused by a click, and there is nothing to keep.
 *
 * Merging them would mean an alarm store carrying entries with no alarm, and a bell whose
 * unread count included "wrong password".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY TOP RIGHT, WHEN ALARMS ARE BOTTOM RIGHT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The alarm stack sits bottom-right for a stated reason: the top right of the console is
 * where the site selector, the compliance chip, the bell and the account menu live, and a
 * stack there covers the controls an operator reaches for when an alarm arrives.
 *
 * These are for the sign-in screen, which has none of that chrome — the top right is empty,
 * and it is where a person's eye goes after pressing a button that did nothing. The two
 * positions do not compete because the two screens never coexist.
 */
import { create } from 'zustand';

export type ToastTone = 'error' | 'success' | 'info';

/** How long each tone stays, in ms. */
export const TOAST_LIFE: Record<ToastTone, number> = {
  /*
   * Errors last longest and are the reason for the timer being VISIBLE. A message about a
   * failed sign-in that vanishes while somebody is still reading it has told them nothing,
   * and they cannot ask for it back — so the countdown is drawn, and hovering stops it.
   */
  error: 8_000,
  success: 4_500,
  info: 5_000,
};

export interface Toast {
  /** Unique per ARRIVAL: the same message twice is two events. */
  key: string;
  tone: ToastTone;
  text: string;
  /** Optional second line, for the action to take. */
  hint?: string | undefined;
}

interface ToastState {
  toasts: Toast[];
  push: (tone: ToastTone, text: string, hint?: string) => void;
  dismiss: (key: string) => void;
  clear: () => void;
}

/**
 * Capped, for the reason the alarm stack was capped after it grew off the top of the
 * viewport: a fixed column with no overflow handling is unusable the moment it exceeds the
 * screen, and the dismiss buttons go with it.
 */
export const MAX_TOASTS = 3;

let seq = 0;

export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],

  push: (tone, text, hint) => {
    const { toasts } = get();
    /*
     * A repeat of the message already on screen is not a second event. Pressing sign-in
     * twice with the same wrong password should not stack two identical toasts — that reads
     * as two different failures.
     */
    if (toasts.some((t) => t.text === text && t.tone === tone)) return;

    seq += 1;
    set({
      toasts: [...toasts, { key: `t${seq}`, tone, text, ...(hint ? { hint } : {}) }].slice(
        -MAX_TOASTS,
      ),
    });
  },

  dismiss: (key) => set({ toasts: get().toasts.filter((t) => t.key !== key) }),
  clear: () => set({ toasts: [] }),
}));

/** Convenience wrappers, so call sites read as what they mean. */
export const toast = {
  error: (text: string, hint?: string) => useToasts.getState().push('error', text, hint),
  success: (text: string, hint?: string) => useToasts.getState().push('success', text, hint),
  info: (text: string, hint?: string) => useToasts.getState().push('info', text, hint),
};
